import { LinkedinMoney } from "../types/linkedin";

export const formatStatus = (status: string): string => {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const formatCurrency = (
  amount: number,
  currency: string = "USD",
  { maximumFractionDigits }: { maximumFractionDigits?: number } = {},
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: maximumFractionDigits ?? 2,
    maximumFractionDigits: maximumFractionDigits ?? 2,
  }).format(amount);
};

export const formatMoney = (money: LinkedinMoney | null) => {
  if (!money) {
    return "—";
  }
  return formatCurrency(money.amount, money["currency-code"]);
};

export type DateInput = Date | string | number;

// ICU puts either a plain space or a narrow no-break space before the
// meridiem depending on its version, so normalize: the text a user sees and
// copies shouldn't depend on which browser or Node is rendering it.
const NARROW_NO_BREAK_SPACE = /\u202f/g;

const DAY: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
const TIME: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

// Omitting `timeZone` formats in the viewer's own zone.
const formatIn = (
  date: DateInput,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
): string =>
  new Intl.DateTimeFormat("en-US", { ...options, timeZone })
    .format(new Date(date))
    .replace(NARROW_NO_BREAK_SPACE, " ");

/** "Aug 12" */
export const formatDay = (date: DateInput, timeZone?: string): string =>
  formatIn(date, timeZone, DAY);

/** "Aug 12, 2026" */
export const formatDayWithYear = (date: DateInput, timeZone?: string): string =>
  formatIn(date, timeZone, { ...DAY, year: "numeric" });

/** "Aug 12 · 3:45 PM" */
export const formatDayTime = (date: DateInput, timeZone?: string): string =>
  `${formatIn(date, timeZone, DAY)} · ${formatIn(date, timeZone, TIME)}`;

/** "Aug 12 · 3:45 PM EDT" */
export const formatDayTimeWithZone = (
  date: DateInput,
  timeZone?: string,
): string =>
  `${formatIn(date, timeZone, DAY)} · ${formatIn(date, timeZone, {
    ...TIME,
    timeZoneName: "short",
  })}`;

const SHORT_DISTANCE_UNITS: [string, number][] = [
  ["y", 365 * 24 * 60 * 60 * 1000],
  ["mo", 30 * 24 * 60 * 60 * 1000],
  ["d", 24 * 60 * 60 * 1000],
  ["h", 60 * 60 * 1000],
  ["m", 60 * 1000],
];

export const formatDistanceShort = (
  date: DateInput,
  baseDate: Date = new Date(),
): string => {
  const ms = Math.max(0, baseDate.getTime() - new Date(date).getTime());
  const idx = SHORT_DISTANCE_UNITS.findIndex(([, size]) => ms >= size);
  if (idx === -1) {
    return "1m ago";
  }

  const [label, size] = SHORT_DISTANCE_UNITS[idx];
  return `${Math.floor(ms / size)}${label} ago`;
};
